import React from 'react';
import { AnyObject, getGatewayDomain, ResponseError } from '@hpfe/common-base';
import { message } from 'antd';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { Config } from '@/common/context/config';
import { User } from '@/common/context/user';
import { downloadFile } from '@/common/utils/helper';
import i18n from '@/i18n';
import DingTalkLogin from '@/service/ding-talk-login';
import { RequestBase } from '@/utils/request-base';
import { safeDecode } from '@/utils/safe';

/**
 * AI.GEN lines=1-15
 * 响应码常量
 */
const RESPONSE_CODE_SUCCESS = 1;
const RESPONSE_CODE_UNAUTHORIZED = 401;
const RESPONSE_CODE_LOGIN_EXPIRED = 6;
const RESPONSE_CODE_WARNING = 18;
const RESPONSE_CODE_ERROR = -1;

/**
 * 字符串常量
 */
const CONTENT_TYPE_JSON = 'application/json';
const CONTENT_DISPOSITION_ATTACHMENT = 'attachment; filename';

/**
 * 延迟时间常量（毫秒）
 */
const LOGIN_REDIRECT_DELAY_401 = 1000;
const LOGIN_REDIRECT_DELAY_6 = 2000;

interface RequestConfig<D> extends AxiosRequestConfig<D> {
  autoDownload?: boolean;
  disableErrorMsg?: boolean; // 禁用错误提示，参考旧调度项目
  returnFullResponse?: boolean; // 返回完整响应对象（包含 code、data、message），默认 false 返回 data
}

/**
 * AI.GEN lines=1-1
 * 请求方法类型 - 从 AxiosInstance 中提取
 */
type RequestMethod = Pick<
  AxiosInstance,
  'get' | 'post' | 'put' | 'delete' | 'patch' | 'options'
>;

export class Request extends RequestBase {
  get: RequestMethod['get'];
  post: RequestMethod['post'];
  put: RequestMethod['put'];
  delete: RequestMethod['delete'];
  patch: RequestMethod['patch'];
  options: RequestMethod['options'];

  private userInstance: User | null = null;

  constructor() {
    super({
      baseURL: '',
    });

    this.get = this.axios.get;
    this.post = this.axios.post;
    this.put = this.axios.put;
    this.delete = this.axios.delete;
    this.patch = this.axios.patch;
    this.options = this.axios.options;
  }

  init(options: { config: Config; user: User }) {
    this.userInstance = options.user;

    // 初始化baseURL
    this.setBaseURL(
      getGatewayDomain(
        options.config.environmentName,
        options.config.countryCode,
      ) || '',
    );
  }

  requestInterceptorFulfilled(config: RequestConfig<any>) {
    const result = super.requestInterceptorFulfilled(config as any);
    const headers = {
      ...(result.headers || {}),
      network: (navigator?.connection as any)?.effectiveType || 'unknown',
    } as AnyObject;

    // 优先使用 headerMaps 中传入的 token，如果没有则使用 DingTalkLogin 的 token，最后使用 userInstance 的 token
    if (!headers.token) {
      const token = DingTalkLogin.getToken() || this.userInstance?.userToken;
      if (token) {
        headers.token = token;
      }
    }
    if (config.autoDownload) {
      config.responseType = 'blob';
    }

    return Object.assign(result, {
      headers,
      originData: config.data,
    });
  }

  responseInterceptorFulfilled(response: AxiosResponse): any {
    const result = super.responseInterceptorFulfilled(response);
    const config = result.config as RequestConfig<any>;

    return new Promise(async (resolve, reject) => {
      if (
        (response.headers['content-type'] || '').indexOf(CONTENT_TYPE_JSON) !==
        -1
      ) {
        // axios 已经自动解析了 JSON，result.data 就是后端返回的完整 JSON 对象，包含 code、data、message 字段
        // responseData 就是包含 code 的完整响应对象，与老项目的 response 一致
        const responseData = result.data;

        // 使用旧调度项目的 code 码处理逻辑
        if (responseData.code === RESPONSE_CODE_SUCCESS) {
          // 成功：code === 1
          // 根据 returnFullResponse 配置决定返回完整响应还是只返回 data
          if (config.returnFullResponse) {
            // 返回完整的响应对象（包含 code、data、message）
            return resolve(responseData);
          }
          // 默认返回业务数据
          return resolve(responseData.data);
        }

        // 登录失效：code === 401 || code === 6
        if (
          responseData.code === RESPONSE_CODE_UNAUTHORIZED ||
          responseData.code === RESPONSE_CODE_LOGIN_EXPIRED
        ) {
          DingTalkLogin.removeToken();
          const errorMsg = responseData.message || i18n.t('error.loginExpired');
          if (!config.disableErrorMsg) {
            message.error(errorMsg);
          }
          setTimeout(
            () => {
              DingTalkLogin.jumpToLogin();
            },
            responseData.code === RESPONSE_CODE_UNAUTHORIZED
              ? LOGIN_REDIRECT_DELAY_401
              : LOGIN_REDIRECT_DELAY_6,
          );
          // 老项目：reject 整个 response 对象（包含 code、data、message）
          return reject(responseData);
        }

        // 特殊处理：code === 18（resolve 但显示错误消息，支持多行显示）
        if (responseData.code === RESPONSE_CODE_WARNING) {
          // 老调度项目：先 resolve，再显示错误消息
          // 根据 returnFullResponse 配置决定返回完整响应还是只返回 data
          if (config.returnFullResponse) {
            resolve(responseData);
          } else {
            resolve(responseData.data);
          }
          if (!config.disableErrorMsg && responseData.message) {
            const list = responseData.message.split('\n') ?? [];
            const messageError = list.map((el: any, index: number) => {
              return index === 0
                ? React.createElement('span', { key: index }, el)
                : React.createElement('div', { key: index }, el);
            });
            message.error(messageError);
          }
          return;
        }

        // 其他错误：reject 并显示错误消息
        // 老项目：reject 整个 response 对象（包含 code、data、message）
        const errorMessage =
          responseData.message || this.getDefaultMessage(responseData.code);

        if (!config.disableErrorMsg) {
          message.error(errorMessage);
        }

        return reject(responseData);
      }

      if (config.autoDownload) {
        const headers = response?.headers as any;
        const contentDisposition = headers.get('Content-Disposition');

        let filename = '';
        if (
          contentDisposition &&
          contentDisposition.indexOf(CONTENT_DISPOSITION_ATTACHMENT) > -1
        ) {
          const [_, tempFilename] = contentDisposition.split('=');
          filename = safeDecode(tempFilename);
        }
        downloadFile(result.data, filename.trim());
      }

      return resolve(result.data);
    });
  }

  responseInterceptorRejected(error: axios.AxiosError) {
    const result: ResponseError = {
      code: RESPONSE_CODE_ERROR,
      message: error.message,
    };
    message.error(
      this.getDefaultMessage(error.status) ||
        i18n.t('error.networkError', { code: error.status || '' }),
    );

    return Promise.reject<ResponseError>(result) as any;
  }

  private getDefaultMessage(code?: number) {
    return i18n.t('error.networkError', { code: code || '-' });
  }

  private setBaseURL(baseURL: string) {
    this.axios.defaults.baseURL = baseURL;
  }
}
